import {
  ProductSubCategoryAutocompleteDocument,
  type ProductSubCategoryAutocompleteQuery,
  type ProductSubCategoryAutocompleteQueryVariables,
} from "@/generated/graphql";
import type { AutocompleteFetchParams, AutocompleteFetchResult } from "@k3-tech/react-kit";
import { useCallback } from "react";
import { useClient } from "urql";

export const useProductSubCategoryAutocomplete = () => {
  const urqlClient = useClient();

  const load = useCallback(
    async (page: number, pageSize: number, search: string, moreFilter?: AutocompleteFetchParams['moreFilter']) => {
      const { data } = await urqlClient
        .query<
          ProductSubCategoryAutocompleteQuery,
          ProductSubCategoryAutocompleteQueryVariables
        >(
          ProductSubCategoryAutocompleteDocument,
          {
            qop: {
              pagination: {
                page,
                pageSize,
                withCount: true,
              },
              filters: {
                ...(moreFilter?.() || {}),
                productSubCategoryName: search,
              },
            },
          },
          { suspense: false, requestPolicy: "network-only" },
        )
        .toPromise();

      const rawItems = (data?.productSubCategories?.items ?? []) as NonNullable<
        ProductSubCategoryAutocompleteQuery["productSubCategories"]
      >["items"];
      const items = (rawItems || [])
        .filter((it): it is NonNullable<typeof it> => Boolean(it))
        .map((item) => ({ value: item.productSubCategoryID, label: item.productSubCategoryName, raw: item }));

      return {
        items: items ?? [],
        total: data?.productSubCategories?.pagination?.totalRows ?? 0,
        hasMore: data?.productSubCategories?.pagination?.hasNext ?? false,
      } as AutocompleteFetchResult;
    },
    [urqlClient],
  );

  return { load };
};
